import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useAuth } from '../contexts/AuthContext';
import { SocialLoginButtons } from '../components/SocialLoginButtons';
import { toast } from 'sonner';
import { Eye, EyeOff, ArrowRight, ArrowUpRight } from 'lucide-react';
import { MagneticButton } from '../components/MagneticButton';

// Palette — 3 colors only:
//   #0B0C10 obsidian, #F8F9FA paper, #E3A869 brass
const BRASS = '#E3A869';

const stagger = { hidden: { opacity: 1 }, show: { transition: { staggerChildren: 0.07, delayChildren: 0.12 } } };
const fadeUp = { hidden: { opacity: 0, y: 18 }, show: { opacity: 1, y: 0, transition: { duration: 0.7, ease: [0.22, 1, 0.36, 1] } } };

export const SignupPage = () => {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const { signup } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await signup(name, email, password);
      toast.success('Account created successfully!');
      navigate('/dashboard');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Signup failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0B0C10] text-[#F8F9FA] overflow-x-hidden" style={{ fontFamily: 'Manrope, sans-serif' }}>
      {/* Header */}
      <header className="fixed top-0 inset-x-0 z-50 backdrop-blur-xl bg-[#0B0C10]/80 border-b border-white/5">
        <div className="max-w-6xl mx-auto px-6 sm:px-10 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-baseline gap-2" data-testid="signup-logo">
            <span className="text-xl font-semibold tracking-tight text-white" style={{ fontFamily: 'Playfair Display, serif' }}>RealProp</span>
            <span className="hidden sm:inline text-[10px] uppercase tracking-[0.25em] text-[#8A8D98]">Studio</span>
          </Link>
          <Link
            to="/login"
            className="text-sm text-[#8A8D98] hover:text-white transition-colors inline-flex items-center gap-1"
            data-testid="header-login-link"
          >
            Sign in
            <ArrowUpRight className="h-3.5 w-3.5" />
          </Link>
        </div>
      </header>

      <main className="min-h-screen flex items-center justify-center px-6 pt-28 pb-16">
        <motion.div
          initial="hidden"
          animate="show"
          variants={stagger}
          className="w-full max-w-md"
        >
          <motion.div variants={fadeUp} className="text-[10px] uppercase tracking-[0.3em] text-[#8A8D98] mb-8">
            <span style={{ color: BRASS }}>●</span>&nbsp;&nbsp;Get started
          </motion.div>

          <motion.h1
            variants={fadeUp}
            className="text-5xl sm:text-6xl leading-[0.95] tracking-tight font-light"
            style={{ fontFamily: 'Outfit, sans-serif' }}
          >
            Create your <span style={{ fontFamily: 'Playfair Display, serif', fontStyle: 'italic', color: BRASS }}>studio.</span>
          </motion.h1>

          <motion.p variants={fadeUp} className="mt-5 text-sm text-[#8A8D98]">
            Listing to published in ten seconds.
          </motion.p>

          <motion.div variants={fadeUp} className="mt-10">
            <SocialLoginButtons />
          </motion.div>

          <motion.div variants={fadeUp} className="my-8 flex items-center gap-3 text-[10px] uppercase tracking-[0.25em] text-[#8A8D98]">
            <span className="flex-1 h-px bg-white/10" />
            or with email
            <span className="flex-1 h-px bg-white/10" />
          </motion.div>

          <motion.form variants={fadeUp} onSubmit={handleSubmit} className="space-y-5" data-testid="signup-form">
            <div>
              <label htmlFor="name" className="text-[10px] text-[#8A8D98] uppercase tracking-[0.25em]">Full name</label>
              <input
                id="name"
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
                className="mt-2 w-full h-12 bg-transparent border-0 border-b border-white/15 text-white text-base placeholder-[#5b5e68] focus:outline-none focus:border-[#E3A869] transition-colors"
                data-testid="signup-name-input"
              />
            </div>
            <div>
              <label htmlFor="email" className="text-[10px] text-[#8A8D98] uppercase tracking-[0.25em]">Email</label>
              <input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="mt-2 w-full h-12 bg-transparent border-0 border-b border-white/15 text-white text-base placeholder-[#5b5e68] focus:outline-none focus:border-[#E3A869] transition-colors"
                data-testid="signup-email-input"
              />
            </div>
            <div>
              <label htmlFor="password" className="text-[10px] text-[#8A8D98] uppercase tracking-[0.25em]">Password</label>
              <div className="relative">
                <input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  minLength={8}
                  className="mt-2 w-full h-12 pr-10 bg-transparent border-0 border-b border-white/15 text-white text-base placeholder-[#5b5e68] focus:outline-none focus:border-[#E3A869] transition-colors"
                  data-testid="signup-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-0 bottom-3 text-[#8A8D98] hover:text-white transition-colors"
                  data-testid="toggle-password-visibility"
                  aria-label="Toggle password visibility"
                >
                  {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
              <p className="text-[11px] text-[#5b5e68] mt-2">Minimum 8 characters.</p>
            </div>

            <MagneticButton
              type="submit"
              disabled={loading}
              className="group w-full h-12 inline-flex items-center justify-center gap-2 rounded-full text-sm font-medium text-[#0B0C10] hover:opacity-90 disabled:opacity-50 transition-opacity mt-2"
              style={{ background: BRASS }}
              data-testid="signup-submit-button"
            >
              {loading ? 'Creating account…' : (
                <>
                  Create account
                  <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" />
                </>
              )}
            </MagneticButton>
          </motion.form>

          <motion.p variants={fadeUp} className="text-sm text-[#8A8D98] text-center mt-10">
            Already a member?{' '}
            <Link to="/login" className="text-white hover:underline underline-offset-4 decoration-[#E3A869]" data-testid="login-link">
              Sign in
            </Link>
          </motion.p>

          <motion.p variants={fadeUp} className="text-[11px] text-[#5b5e68] text-center mt-6 leading-relaxed">
            By creating an account you agree to our{' '}
            <Link to="/terms" className="underline underline-offset-2 hover:text-[#8A8D98]">Terms</Link>{' '}and{' '}
            <Link to="/privacy" className="underline underline-offset-2 hover:text-[#8A8D98]">Privacy</Link>.
          </motion.p>
        </motion.div>
      </main>
    </div>
  );
};

export default SignupPage;
